import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MenuIcon, XIcon } from "lucide-react";
import { DISCORD_URL } from "@/lib/config";

function DiscordIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.2a.077.077 0 0 0-.082.038c-.355.63-.752 1.453-1.029 2.1a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-1.045-2.1.08.08 0 0 0-.081-.038A19.736 19.736 0 0 0 5.075 4.37a.07.07 0 0 0-.032.027C1.93 9.046 1.066 13.58 1.49 18.057a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.127c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.073.073 0 0 1 .079.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127c-.598.349-1.22.645-1.873.891a.077.077 0 0 0-.041.106c.36.699.771 1.364 1.225 1.993a.077.077 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.661a.061.061 0 0 0-.031-.029ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.955 2.42-2.157 2.42Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.176 1.094 2.157 2.418 0 1.334-.946 2.42-2.157 2.42Z" />
    </svg>
  );
}

const links = [
  { href: "#features", label: "Features" },
  { href: "#how-to-play", label: "How to Play" },
  { href: "#stats", label: "Server" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-white/[0.06] bg-black/60 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo"
            className="flex items-center gap-2.5 group"
          >
            <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_8px_28px_-8px_rgba(250,204,21,0.5)]">
              <img
                src="https://cdn.runeria.fun/icon.png"
                alt="OriginPS"
                className="h-full w-full object-cover"
                draggable="false"
              />
              <span className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-b from-white/30 via-transparent to-black/30" />
              <span className="pointer-events-none absolute inset-x-1.5 top-1 h-1.5 rounded-full bg-white/40 blur-[1px]" />
            </span>
            <span className="font-display text-lg font-semibold tracking-tight text-white">
              Origin<span className="text-amber-400">PS</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="relative px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
                <span className="absolute inset-x-3 -bottom-px h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent opacity-0 hover:opacity-100 transition-opacity" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-join-discord"
              className="hidden md:inline-flex items-center gap-2 rounded-full ghost-pill px-4 py-2 text-sm font-medium transition-all hover:translate-y-[-1px]"
            >
              <DiscordIcon className="h-4 w-4 text-indigo-300" />
              Join Discord
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/10 bg-white/5 text-white"
              aria-label="Toggle menu"
            >
              {open ? <XIcon className="h-4 w-4" /> : <MenuIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="md:hidden pb-4 pt-2 space-y-2"
            data-testid="nav-mobile-menu"
          >
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block rounded-xl px-4 py-3 text-sm text-slate-200 bg-white/[0.03] border border-white/[0.06]"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-mobile-join-discord"
              className="block rounded-xl px-4 py-3 text-sm font-medium bg-white text-black text-center"
            >
              Join Discord
            </a>
          </div>
        )}
      </div>
    </motion.header>
  );
}
